import { useMemo } from 'react'
import { buildTasteProfile, scoreShow, BADGE_FLOOR } from '../lib/reelMatch'

// Module-level cache: the profile is a pure function of the watchlist arrays,
// and wl.items/ratings/statuses have stable identities between renders
// (useWatchlist memoizes). Keyed on the items array so every card in a grid
// shares ONE profile build instead of recomputing per card.
const cache = new WeakMap()
export function profileFor(wl) {
  if (!wl?.items?.length) return null
  let p = cache.get(wl.items)
  if (!p) {
    p = buildTasteProfile({ items: wl.items, ratings: wl.ratings, statuses: wl.statuses })
    cache.set(wl.items, p)
  }
  return p
}

// Poster-corner pill: "82% match". Renders nothing when the profile is too
// thin (honesty rule), the score is below BADGE_FLOOR, or the show is already
// in the list (you don't need a match score for a show you track).
export function MatchBadge({ show, wl, style }) {
  const score = useMemo(() => {
    if (!wl || wl.has?.(show.id)) return null
    const p = profileFor(wl)
    if (!p?.enough) return null
    const s = scoreShow(p, show)
    return s.pct != null && s.pct >= BADGE_FLOOR ? s.pct : null
  }, [wl, show])
  if (score == null) return null
  const strong = score >= 75
  return (
    <div aria-label={`${score}% match for your taste`} style={{
      position:'absolute', top:7, left:7, zIndex:3,
      background:'rgba(var(--bg-rgb),0.78)', backdropFilter:'blur(6px)',
      border:`1px solid ${strong ? 'rgba(var(--amber-rgb),0.55)' : 'var(--border)'}`,
      borderRadius:6, padding:'2px 6px',
      fontSize:10, fontWeight:800, letterSpacing:'0.03em',
      color: strong ? 'var(--amber)' : 'var(--text)',
      lineHeight:1.4, pointerEvents:'none',
      ...style,
    }}>{score}%</div>
  )
}

// ShowDetail's About-tab strip: the score WITH its receipts. Unlike the poster
// badge this renders at any score (an honest 40% is useful before you commit) —
// but still never on a thin profile, and never for shows already in the list.
export function MatchStrip({ show, wl }) {
  const result = useMemo(() => {
    if (!wl || wl.has?.(show.id)) return null
    const p = profileFor(wl)
    if (!p?.enough) return null
    const s = scoreShow(p, show)
    return s.pct != null ? s : null
  }, [wl, show])
  if (!result) return null
  const { pct, reasons } = result
  const strong = pct >= 75
  return (
    <div style={{
      marginBottom:14, display:'flex', alignItems:'center', gap:12,
      background: strong ? 'rgba(var(--amber-rgb),0.06)' : 'var(--bg-card, rgba(var(--ink-rgb),0.03))',
      border:`1px solid ${strong ? 'rgba(var(--amber-rgb),0.25)' : 'var(--border)'}`,
      borderRadius:'var(--radius-md)', padding:'11px 14px',
    }}>
      <div style={{ flexShrink:0, textAlign:'center' }}>
        <div style={{ fontSize:22, fontWeight:800, lineHeight:1, color: strong ? 'var(--amber)' : 'var(--text)' }}>{pct}%</div>
        <div style={{ fontSize:8.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-dim)', marginTop:3 }}>match</div>
      </div>
      <div style={{ fontSize:12, color:'var(--text-dim)', lineHeight:1.5 }}>
        {reasons.length
          ? reasons.map((r, i) => (
              <span key={r.label}>
                {i > 0 && ' · '}
                {r.positive ? `You rate ${r.label} highly` : `${r.label} hasn't landed for you`}
              </span>
            ))
          : 'Scored from your ratings, finishes, and drops.'}
      </div>
    </div>
  )
}
